import { Amplify, type ResourcesConfig } from 'aws-amplify';
import { cognitoUserPoolsTokenProvider } from 'aws-amplify/auth/cognito';
import * as Sentry from '@sentry/react';
import { AUTH_REGION_URL, SSO_CONFIG_JSON } from '@/constants/env.constants';
import { type ActiveAuthRegion, readCachedAuthRegion, resolveActiveAuthRegion } from './active-region';

const { logger } = Sentry;

type AuthResourcesConfig = NonNullable<ResourcesConfig['Auth']>;

/**
 * Amplify JS derives the Cognito endpoint from the user pool ID prefix
 * (`us-east-2_dzf28yHC9` -> `cognito-idp.us-east-2.amazonaws.com`) and drops both
 * `aws_region` and `user_pool_endpoint` when it parses the Gen 2 outputs shape. The
 * only knob that moves the endpoint is `Auth.Cognito.userPoolEndpoint` on the
 * `ResourcesConfig` object, which is what this module sets.
 *
 * Re-configuring mid-session is supported and does not sign anyone out: token
 * storage keys are `CognitoIdentityServiceProvider.<userPoolClientId>.*`, with no
 * Region in them.
 */
const REGION_RECHECK_INTERVAL_MS = 60_000;

const RETRYABLE_ERROR_NAMES = ['NetworkError', 'ServiceUnavailableException', 'InternalErrorException'];

let activeRegion: ActiveAuthRegion | null = null;
let watcherStarted = false;
let lastCheckedAt = 0;
let inFlightRefresh: { force: boolean; promise: Promise<boolean> } | null = null;

/** The Region currently configured, or null while the bundled default is in use. */
export const getActiveAuthRegion = () => activeRegion;

/**
 * True while a replica is serving. Sign-in and token refresh work; password reset,
 * TOTP setup, MFA preference writes, profile edits and sign-up do not.
 */
export const isAuthReadOnly = () => activeRegion?.isReadOnly === true;

const applyAuthRegion = (region: ActiveAuthRegion): boolean => {
  const config = Amplify.getConfig();
  const auth = config.Auth;
  const cognito = auth?.Cognito;

  // Identity-pool-only configs have no user pool endpoint to override.
  if (!auth || !cognito || !('userPoolId' in cognito)) {
    return false;
  }

  activeRegion = region;

  if (cognito.userPoolEndpoint === region.userPoolEndpoint) {
    return false;
  }

  const patched: ResourcesConfig = {
    ...config,
    // Amplify models Auth.Cognito as a StrictUnion of the user-pool and
    // identity-pool shapes, and a spread of a union member no longer satisfies it
    // even after narrowing. The narrowing above is what makes this safe.
    Auth: { ...auth, Cognito: { ...cognito, userPoolEndpoint: region.userPoolEndpoint } } as AuthResourcesConfig,
  };

  Amplify.configure(patched);

  cognitoUserPoolsTokenProvider.setAuthConfig(patched.Auth as AuthResourcesConfig);

  return true;
};

const resolveAndApplyRegion = async (force: boolean): Promise<boolean> => {
  lastCheckedAt = Date.now();

  const resolved = await resolveActiveAuthRegion({ force });

  if (!resolved) {
    return false;
  }

  const switched = applyAuthRegion(resolved);

  if (switched) {
    logger.warn('Cognito region switched', { region: resolved.region, readOnly: resolved.isReadOnly });
  }

  return switched;
};

/**
 * Re-reads the document and re-points Amplify if the active Region moved. Returns
 * true only when the endpoint actually changed, so callers can decide whether a
 * retry is worth attempting.
 *
 * Concurrent callers share one network read. Every API request passes through the
 * axios interceptor, so a Region going down would otherwise fan out into one forced
 * document fetch per in-flight request. A forced call joins an in-flight forced call
 * but never an unforced one, which could still be answered from the HTTP cache.
 */
export const refreshAuthRegion = async ({ force = false } = {}): Promise<boolean> => {
  if (inFlightRefresh && (inFlightRefresh.force || !force)) {
    return inFlightRefresh.promise;
  }

  const promise = resolveAndApplyRegion(force).finally(() => {
    if (inFlightRefresh?.promise === promise) {
      inFlightRefresh = null;
    }
  });

  inFlightRefresh = { force, promise };

  return promise;
};

const startAuthRegionWatcher = () => {
  if (!AUTH_REGION_URL || watcherStarted || !globalThis.document) {
    return;
  }

  watcherStarted = true;

  // A long-lived tab should pick up a failover without the user reloading. Focus is
  // the cheap trigger — no polling, and it fires exactly when the user is about to
  // do something that needs a working endpoint.
  globalThis.document.addEventListener('visibilitychange', () => {
    if (globalThis.document.visibilityState !== 'visible') {
      return;
    }

    if (Date.now() - lastCheckedAt < REGION_RECHECK_INTERVAL_MS) {
      return;
    }

    // Fire and forget: a failed recheck is not an error path, the next focus retries.
    refreshAuthRegion().catch(() => undefined);
  });
};

export const initAmplifyAuth = async () => {
  const configJson = JSON.parse(SSO_CONFIG_JSON);

  // Configure with the bundled outputs first: this is how Amplify parses the Gen 2
  // shape for us, so getConfig() below returns a complete ResourcesConfig to patch.
  Amplify.configure(configJson);

  // Seed synchronously from the last known good value, so even a failed or slow
  // fetch leaves us pointed at the Region we were last told about.
  const cached = readCachedAuthRegion();

  if (cached) {
    applyAuthRegion(cached);
  }

  // Awaited, not fired and forgotten: AuthProvider calls fetchAuthSession as soon as
  // it mounts, and a token refresh sent to a Region that is down signs the user out.
  // The fetch is hard-bounded by AUTH_REGION_FETCH_TIMEOUT_MS.
  const resolved = await resolveActiveAuthRegion();

  if (resolved) {
    applyAuthRegion(resolved);
  }

  startAuthRegionWatcher();
};

const isRetryableAuthError = (error: unknown): boolean => {
  if (!error || typeof error !== 'object') {
    return false;
  }

  const { name, message } = error as { name?: unknown; message?: unknown };

  if (typeof name === 'string' && RETRYABLE_ERROR_NAMES.includes(name)) {
    return true;
  }

  const status = (error as { $metadata?: { httpStatusCode?: number } }).$metadata?.httpStatusCode;

  if (typeof status === 'number' && status >= 500) {
    return true;
  }

  return typeof message === 'string' && /network error/i.test(message);
};

/**
 * Bridges the detection window: Cognito can fail over minutes before the health
 * check catches up, so a transport-level failure is worth one re-resolve and one
 * retry. Anything the service answered (NotAuthorizedException, expired code) is
 * rethrown untouched.
 *
 * Only wrap operations that *start* a flow — signIn, fetchAuthSession, getCurrentUser,
 * signOut. Never wrap confirmSignIn or any other challenge continuation: the Session
 * handed out by InitiateAuth belongs to the Region that issued it, so retrying it
 * against the other Region cannot succeed.
 */
export const withAuthRegionFailover = async <T>(operation: () => Promise<T>): Promise<T> => {
  try {
    return await operation();
  } catch (error) {
    if (!isRetryableAuthError(error)) {
      throw error;
    }

    const switched = await refreshAuthRegion({ force: true });

    if (!switched) {
      throw error;
    }

    logger.warn('Retrying auth operation against the newly active Cognito region', {
      region: activeRegion?.region,
    });

    return operation();
  }
};
